import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useUser } from '../../../../UserContext';
import { getSubscriptionLabel, hasPaidPlan, isPremiumStatus, isVenueStatus } from '../../../../utils/subscriptionStatus';
import { getMembershipCapabilities } from '../../../../utils/membershipCapabilities';
import { appConfig } from '../../../config/appConfig';
import { subscriptionService } from '../../../../services';
import './Subscription.css';
import {
    ArrowRight,
    Building2,
    CalendarClock,
    CheckCircle,
    Clock3,
    Crown,
    Info,
    Settings,
    Target,
    Users,
    XCircle,
    Zap
} from 'lucide-react';

const fetchSubscriptionDetails = async () => {
    const data = await subscriptionService.getStatus();
    return {
        status: getSubscriptionLabel(data?.status),
        startDate: data?.startDate || null,
        endDate: data?.endDate || null,
        plan: data?.plan || null,
        isAnnual: Boolean(data?.isAnnual),
        entitlement: data?.entitlement || null,
        isActive: typeof data?.isActive === 'boolean' ? data.isActive : null,
        currentPeriodEnd: data?.currentPeriodEnd || null,
        willRenew: typeof data?.willRenew === 'boolean' ? data.willRenew : null,
        graceState: data?.graceState || 'none',
        serverTime: data?.serverTime || null
    };
};

export const isVIP = (subscriptionStatus) => isPremiumStatus(subscriptionStatus);
export const isVenue = (subscriptionStatus) => isVenueStatus(subscriptionStatus);

export const VIPCheck = ({ children, fallback }) => {
    const { user } = useUser();
    if (!user || !user.subscription?.status) {
        return null;
    }

    return isVIP(user.subscription) ? children : fallback;
};

const Subscription = () => {
    const { user } = useUser();
    const [isLoading, setIsLoading] = useState(false);
    const [isUserLoading, setIsUserLoading] = useState(true);
    const [billingCycle, setBillingCycle] = useState('annual');
    const [error, setError] = useState(null);
    const [promoCode, setPromoCode] = useState('');
    const [promoFeedback, setPromoFeedback] = useState(null);
    const [isValidatingPromo, setIsValidatingPromo] = useState(false);
    const [subscriptionDetails, setSubscriptionDetails] = useState({
        status: null,
        startDate: null,
        endDate: null,
        plan: null,
        isAnnual: false,
        entitlement: null,
        isActive: null,
        currentPeriodEnd: null,
        willRenew: null,
        graceState: 'none',
        serverTime: null
    });

    const refreshSubscriptionDetails = async () => {
        setError(null);
        try {
            const details = await fetchSubscriptionDetails();
            setSubscriptionDetails(details);
        } catch (fetchError) {
            console.error('Error fetching subscription status:', fetchError);
            setError('Failed to fetch subscription status. Please try again later.');
        }
    };

    useEffect(() => {
        if (!user) {
            setIsUserLoading(true);
            return;
        }

        setSubscriptionDetails((previous) => ({
            ...previous,
            status: getSubscriptionLabel(user?.subscription || 'Free'),
            startDate: user?.subscription?.startDate || previous.startDate || null,
            endDate: user?.subscription?.endDate || previous.endDate || null,
            entitlement: user?.subscription?.entitlement || previous.entitlement || null,
            isActive: typeof user?.subscription?.isActive === 'boolean' ? user.subscription.isActive : previous.isActive,
            currentPeriodEnd: user?.subscription?.currentPeriodEnd || previous.currentPeriodEnd || null,
            willRenew: typeof user?.subscription?.willRenew === 'boolean' ? user.subscription.willRenew : previous.willRenew,
            graceState: user?.subscription?.graceState || previous.graceState || 'none',
            serverTime: user?.subscription?.serverTime || previous.serverTime || null,
            plan: user?.subscription?.plan || previous.plan || null,
            isAnnual: typeof user?.subscription?.isAnnual === 'boolean' ? user.subscription.isAnnual : previous.isAnnual
        }));
        setIsUserLoading(false);
        refreshSubscriptionDetails();
    }, [user]);

    const effectiveSubscription = {
        ...(user?.subscription || {}),
        ...subscriptionDetails,
        status: subscriptionDetails.status || user?.subscription?.status || 'Free'
    };
    const subscriptionStatus = getSubscriptionLabel(effectiveSubscription);
    const capabilities = getMembershipCapabilities(effectiveSubscription);
    const poundSign = '\u00A3';
    const monthlyPrice = 7.99;
    const annualPrice = 79.99;
    const annualMonthlyEquivalent = (annualPrice / 12).toFixed(2);
    const isPaid = hasPaidPlan(effectiveSubscription);
    const hasVenueManagementContext = Boolean(user?.isAdmin || (user?.clubAdminCount || 0) > 0 || isVenue(effectiveSubscription));
    const isFreeMemberView = !isPaid;
    const isPremiumMemberView = isVIP(effectiveSubscription);

    const venueManagementPath = user?.clubAdminCount > 0 || user?.isAdmin ? '/dashboard/manage-clubs' : '/dashboard/clubs';
    const lifecyclePeriodEnd = subscriptionDetails.currentPeriodEnd || subscriptionDetails.endDate;
    const renewalDate = lifecyclePeriodEnd ? new Date(lifecyclePeriodEnd) : null;
    const daysRemaining = renewalDate ? Math.max(0, Math.ceil((renewalDate.getTime() - Date.now()) / (1000 * 60 * 60 * 24))) : null;
    const lifecycleGraceState = (subscriptionDetails.graceState || 'none').toLowerCase();
    const lifecycleWillRenew = typeof effectiveSubscription.willRenew === 'boolean' ? effectiveSubscription.willRenew : isPaid;

    const memberComparisonRows = useMemo(() => ([
        { label: 'Marketplace access', freeValue: 'Included', premiumValue: 'Included', freeIncluded: true, premiumIncluded: true },
        { label: 'Blogs and news', freeValue: 'Included', premiumValue: 'Included', freeIncluded: true, premiumIncluded: true },
        { label: 'Venue discovery', freeValue: 'Included', premiumValue: 'Included', freeIncluded: true, premiumIncluded: true },
        { label: 'Friends network', freeValue: 'Included', premiumValue: 'Included', freeIncluded: true, premiumIncluded: true },
        { label: 'Expanded social insights', freeValue: 'Not included', premiumValue: 'Included', freeIncluded: false, premiumIncluded: true },
        { label: 'Equipment records', freeValue: 'Included', premiumValue: 'Included', freeIncluded: true, premiumIncluded: true },
        { label: 'Richer equipment history/workflows', freeValue: 'Not included', premiumValue: 'Included', freeIncluded: false, premiumIncluded: true },
        { label: 'Gun cabinet workflows', freeValue: 'Included', premiumValue: 'Included', freeIncluded: true, premiumIncluded: true },
        { label: 'Priority gun cabinet value', freeValue: 'Not included', premiumValue: 'Included', freeIncluded: false, premiumIncluded: true },
        { label: 'Connect Perks browsing', freeValue: 'Included', premiumValue: 'Included + premium extras', freeIncluded: true, premiumIncluded: true },
        { label: 'Scoring and event tracking', freeValue: 'Included', premiumValue: 'Included', freeIncluded: true, premiumIncluded: true },
        { label: 'Unlimited scoring volume', freeValue: 'Not included', premiumValue: 'Included', freeIncluded: false, premiumIncluded: true },
        { label: 'Analytics dashboard', freeValue: 'Included', premiumValue: 'Included', freeIncluded: true, premiumIncluded: true },
        { label: 'Advanced analytics depth', freeValue: 'Not included', premiumValue: 'Included', freeIncluded: false, premiumIncluded: true },
        { label: 'Recognition and challenges', freeValue: 'Basic', premiumValue: 'Enhanced', freeIncluded: true, premiumIncluded: true },
        { label: 'Premium-only challenges', freeValue: 'Not included', premiumValue: 'Included', freeIncluded: false, premiumIncluded: true }
    ]), []);

    const venueBenefits = [
        'Premium public venue profile',
        'Member management and targeted updates',
        'Event and shooter operations',
        'Manage stands, ranges, pegs, lanes, and facilities',
        'Connect Perks publishing and claims',
        'Marketplace participation as a venue seller'
    ];

    const handleUpgrade = async () => {
        setIsLoading(true);
        setError(null);
        try {
            const promoCodeToApply = promoCode.trim();
            const data = await subscriptionService.createCheckoutSession({
                isAnnual: billingCycle === 'annual',
                tierType: 'premium',
                promoCode: promoCodeToApply || null,
                clientRequestId: `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`
            });

            if (!data?.url) {
                throw new Error('No checkout URL received from the server');
            }

            window.location.href = data.url;
        } catch (upgradeError) {
            console.error('Error upgrading subscription:', upgradeError);
            setError(upgradeError.message || 'Failed to upgrade subscription. Please try again.');
        } finally {
            setIsLoading(false);
        }
    };

    const handleValidatePromo = async () => {
        const candidate = promoCode.trim();
        if (!candidate) {
            setPromoFeedback({ valid: false, message: 'Enter a promo code to validate.' });
            return;
        }

        setIsValidatingPromo(true);
        setPromoFeedback(null);
        setError(null);

        try {
            const response = await subscriptionService.validatePromoCode(candidate);
            if (response?.valid) {
                setPromoFeedback({
                    valid: true,
                    message: response?.message || 'Promo code is valid and will be applied at checkout.'
                });
                return;
            }

            setPromoFeedback({
                valid: false,
                message: response?.message || 'Promo code is not valid.'
            });
        } catch (promoError) {
            setPromoFeedback({
                valid: false,
                message: promoError?.message || 'Could not validate promo code right now.'
            });
        } finally {
            setIsValidatingPromo(false);
        }
    };

    const handleManageSubscription = async () => {
        setIsLoading(true);
        setError(null);
        try {
            const data = await subscriptionService.createPortalSession();
            if (!data?.url) {
                throw new Error('No portal URL received from the server');
            }

            window.location.href = data.url;
        } catch (portalError) {
            console.error('Error accessing subscription portal:', portalError);
            setError(portalError.message || 'Failed to access subscription management portal. Please try again.');
        } finally {
            setIsLoading(false);
        }
    };

    if (isUserLoading || !subscriptionStatus) {
        return (
            <div className="subscription-page dashboard-component">
                <div className="subscription-loading">
                    <div className="subscription-loading-spinner"></div>
                    <p>Loading subscription details...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="subscription-page">
            <div className="subscription-header">
                <div className="subscription-header-content">
                    <h1>Membership & Venue Plans</h1>
                    <p>
                        Free stays genuinely useful. Premium adds depth for members. Venue plans are a separate
                        product for clubs, grounds, estates, and ranges.
                    </p>
                </div>
            </div>

            {error && (
                <div className="subscription-error">
                    <span>{error}</span>
                    <button onClick={refreshSubscriptionDetails} className="subscription-manage-btn" disabled={isLoading}>
                        Retry
                    </button>
                </div>
            )}

            <div className="subscription-status-card">
                <div className="subscription-status-icon">
                    {isVenue(effectiveSubscription) ? <Building2 size={28} /> : isVIP(effectiveSubscription) ? <Crown size={28} /> : <Zap size={28} />}
                </div>
                <div className="subscription-status-info">
                    <span className="subscription-status-label">Current Plan</span>
                    <span className={`subscription-status-value ${isPaid ? 'premium' : ''}`}>
                        {getSubscriptionLabel(subscriptionStatus)}
                    </span>
                </div>
                <div className="subscription-status-info">
                    <span className="subscription-status-label">What It Means</span>
                    <span className="subscription-status-value">{capabilities.memberSummary}</span>
                </div>
                {isPaid && (
                    <button onClick={handleManageSubscription} className="subscription-manage-btn" disabled={isLoading}>
                        <Settings size={18} />
                        {isLoading ? 'Processing...' : 'Manage Subscription'}
                    </button>
                )}
            </div>

            {isPremiumMemberView && (
                <div className="subscription-retention-card">
                    <div className="subscription-retention-head">
                        <h3>You are getting full Premium value</h3>
                        <p>Unlimited scoring, deeper analytics, premium perks, and stronger recognition tools.</p>
                    </div>
                    <div className="subscription-retention-grid">
                        <div className="subscription-retention-item">
                            <CalendarClock size={18} />
                            <div>
                                <strong>Renews / ends</strong>
                                <span>{renewalDate ? renewalDate.toLocaleDateString() : 'Not available yet'}</span>
                            </div>
                        </div>
                        <div className="subscription-retention-item">
                            <Clock3 size={18} />
                            <div>
                                <strong>Time remaining</strong>
                                <span>{daysRemaining !== null ? `${daysRemaining} day${daysRemaining === 1 ? '' : 's'} left` : 'Not available yet'}</span>
                            </div>
                        </div>
                        <div className="subscription-retention-item">
                            <Info size={18} />
                            <div>
                                <strong>Lifecycle status</strong>
                                <span>
                                    {lifecycleGraceState !== 'none'
                                        ? 'Billing issue/grace state detected. Premium remains active while this state is active.'
                                        : !lifecycleWillRenew
                                            ? 'Cancellation scheduled. Premium remains active until period end.'
                                            : 'Active and set to renew automatically.'}
                                </span>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {isFreeMemberView && (
                <div className="subscription-upgrade-banner">
                    <div className="subscription-upgrade-content">
                        <div className="subscription-upgrade-badge">Free is open by design</div>
                        <h2>Upgrade when you want more power, not because core value is locked</h2>
                        <p>
                            Free includes discovery, marketplace, blogs/news, and core scoring. Premium unlocks unlimited
                            volume, deeper analysis, and premium member experiences.
                        </p>
                    </div>
                </div>
            )}

            {isFreeMemberView && (
                <div className="subscription-billing-toggle">
                    <span className={billingCycle === 'monthly' ? 'active' : ''}>Monthly</span>
                    <button className="billing-toggle-switch" onClick={() => setBillingCycle(billingCycle === 'monthly' ? 'annual' : 'monthly')}>
                        <span className={`toggle-slider ${billingCycle === 'annual' ? 'annual' : ''}`}></span>
                    </button>
                    <span className={billingCycle === 'annual' ? 'active' : ''}>
                        Annual
                        <span className="save-badge">Best value</span>
                    </span>
                </div>
            )}

            {isFreeMemberView && (
                <div className="subscription-upgrade-code">
                    <label htmlFor="member-promo-code">Promo code</label>
                    <input
                        id="member-promo-code"
                        type="text"
                        value={promoCode}
                        onChange={(event) => setPromoCode(event.target.value.toUpperCase())}
                        placeholder="Optional"
                    />
                    <button type="button" onClick={handleValidatePromo} disabled={isValidatingPromo || isLoading}>
                        {isValidatingPromo ? 'Checking...' : 'Validate'}
                    </button>
                    {promoFeedback?.message ? (
                        <span className={promoFeedback.valid ? 'promo-feedback ok' : 'promo-feedback error'}>
                            {promoFeedback.message}
                        </span>
                    ) : (
                        <span className="promo-feedback hint">If valid, this code is applied when you tap Subscribe.</span>
                    )}
                </div>
            )}

            {isFreeMemberView && (
                <div className="subscription-pricing-grid">
                    <article className="subscription-pricing-card current">
                        <h4>Free</h4>
                        <p className="subscription-pricing-price">{poundSign}0.00<span>/month</span></p>
                        <p className="subscription-pricing-copy">You are currently on Free with core scoring, marketplace access, and venue discovery.</p>
                    </article>

                    <article className={`subscription-pricing-card ${billingCycle === 'monthly' ? 'selected' : ''}`}>
                        <h4>Premium Monthly</h4>
                        <p className="subscription-pricing-price">{poundSign}{monthlyPrice.toFixed(2)}<span>/month</span></p>
                        <p className="subscription-pricing-copy">Flexible billing with full premium entitlement while active.</p>
                        <button type="button" className="subscription-pricing-action" onClick={() => setBillingCycle('monthly')}>
                            Choose monthly
                        </button>
                        {billingCycle === 'monthly' && (
                            <button type="button" className="subscription-pricing-subscribe" onClick={handleUpgrade} disabled={isLoading}>
                                {isLoading ? 'Processing...' : 'Subscribe'}
                                <ArrowRight size={16} />
                            </button>
                        )}
                    </article>

                    <article className={`subscription-pricing-card ${billingCycle === 'annual' ? 'selected' : ''}`}>
                        <h4>Premium Annual</h4>
                        <p className="subscription-pricing-price">{poundSign}{annualPrice.toFixed(2)}<span>/year</span></p>
                        <p className="subscription-pricing-copy">{poundSign}{annualMonthlyEquivalent}/month equivalent. 2 months free vs monthly billing.</p>
                        <button type="button" className="subscription-pricing-action" onClick={() => setBillingCycle('annual')}>
                            Choose annual
                        </button>
                        {billingCycle === 'annual' && (
                            <button type="button" className="subscription-pricing-subscribe" onClick={handleUpgrade} disabled={isLoading}>
                                {isLoading ? 'Processing...' : 'Subscribe'}
                                <ArrowRight size={16} />
                            </button>
                        )}
                    </article>
                </div>
            )}

            <div className="subscription-clarity-panel">
                <div className="subscription-clarity-header">
                    <h3>Free vs Premium for members</h3>
                    <p>The difference should be obvious: Free gets you started properly, Premium scales your workflow.</p>
                </div>
                <div className="subscription-clarity-table-wrap">
                    <table className="subscription-clarity-table">
                        <thead>
                            <tr>
                                <th>Capability</th>
                                <th>Free</th>
                                <th>Premium</th>
                            </tr>
                        </thead>
                        <tbody>
                            {memberComparisonRows.map((row) => (
                                <tr key={row.label}>
                                    <td>{row.label}</td>
                                    <td>
                                        <span className={`subscription-clarity-pill ${row.freeIncluded ? 'included' : 'limited'}`}>
                                            {row.freeIncluded ? <CheckCircle size={14} /> : <XCircle size={14} />}
                                            {row.freeValue}
                                        </span>
                                    </td>
                                    <td>
                                        <span className={`subscription-clarity-pill ${row.premiumIncluded ? 'included' : 'limited'}`}>
                                            {row.premiumIncluded ? <CheckCircle size={14} /> : <XCircle size={14} />}
                                            {row.premiumValue}
                                        </span>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>

            <div className="subscription-venue-features">
                <h3 className="subscription-section-title">Venue Plan (Separate From Member Premium)</h3>
                <p className="subscription-section-desc">
                    Ideal for clay grounds, rifle ranges, and game estates. Venue billing and permissions are managed
                    separately from personal member Premium.
                </p>
                <div className="venue-types-inline">
                    <span>Clay Ground</span>
                    <span>Rifle Range</span>
                    <span>Game Estate / Shoot</span>
                </div>
                <div className="venue-features-grid">
                    {venueBenefits.map((benefit) => (
                        <div key={benefit} className="venue-feature-item">
                            <CheckCircle size={20} />
                            <div>
                                <h4>{benefit}</h4>
                            </div>
                        </div>
                    ))}
                </div>
                <div className="venue-claim-flow">
                    <h4>How venue claiming works</h4>
                    <div className="venue-claim-steps">
                        <div><strong>1.</strong> Create a normal Shoot Connect account.</div>
                        <div><strong>2.</strong> Request to claim your venue listing.</div>
                        <div><strong>3.</strong> We verify ownership/authority and complete billing.</div>
                        <div><strong>4.</strong> We assign owner/admin rights to your venue tools.</div>
                    </div>
                </div>
                <div className="venue-cta-section">
                    {hasVenueManagementContext ? (
                        <Link to={venueManagementPath} className="venue-contact-btn">
                            <Users size={18} />
                            Manage your venues
                            <ArrowRight size={18} />
                        </Link>
                    ) : (
                        <Link to="/dashboard/clubs" className="venue-contact-btn">
                            <Building2 size={18} />
                            Search venues and claim listing
                            <ArrowRight size={18} />
                        </Link>
                    )}
                    {!hasVenueManagementContext && (
                        <a href={`mailto:${appConfig.venuesEmail}`} className="venue-secondary-link">
                            Contact venue team
                        </a>
                    )}
                </div>
            </div>

            <div className="subscription-disciplines">
                <h3 className="subscription-section-title">All Disciplines, One Product Spine</h3>
                <p className="subscription-section-desc">Shoot Connect supports member and venue workflows across core disciplines.</p>
                <div className="subscription-disciplines-grid">
                    <div className="discipline-card">
                        <div className="discipline-icon clay">
                            <Target size={26} />
                        </div>
                        <h4>Clay</h4>
                        <p>Sporting, skeet, trap, ABT and more.</p>
                    </div>
                    <div className="discipline-card">
                        <div className="discipline-icon rifle">
                            <Target size={26} />
                        </div>
                        <h4>Rifle</h4>
                        <p>Target, practical, training and scorecards.</p>
                    </div>
                    <div className="discipline-card">
                        <div className="discipline-icon game">
                            <Target size={26} />
                        </div>
                        <h4>Game</h4>
                        <p>Estate and day operations, drives and bag records.</p>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Subscription;
